/**
 * One recorded piece of a dictation that has not reached the composer yet. The audio stays
 * until the piece is transcribed; then only its text is kept, until the text is delivered.
 * Rows outlive a reload or a killed home-screen app, so the speech can still be recovered.
 */
export type StoredDictationSegment = {
  id: string;
  /** Signed-in user the recording belongs to; other accounts on the device never see it. */
  owner: string | null;
  takeId: string;
  index: number;
  createdAt: number;
  spaceId: string | null;
  mimeType: string;
  audio?: ArrayBuffer;
  text?: string;
};

/** Local storage boundary for untranscribed dictation, faked in tests. */
export interface DictationAudioStore {
  put(segment: StoredDictationSegment): Promise<void>;
  delete(ids: string[]): Promise<void>;
  list(): Promise<StoredDictationSegment[]>;
}

const DB_NAME = "hrokbot-dictation-v1";
const STORE_NAME = "segments";

/** Rows written by any build: anything that does not look like a segment is ignored. */
export function isStoredDictationSegment(value: unknown): value is StoredDictationSegment {
  if (typeof value !== "object" || value === null) return false;
  if (!("id" in value) || typeof value.id !== "string") return false;
  if (!("owner" in value) || (value.owner !== null && typeof value.owner !== "string")) {
    return false;
  }
  if (!("takeId" in value) || typeof value.takeId !== "string") return false;
  if (!("index" in value) || typeof value.index !== "number") return false;
  if (!("createdAt" in value) || typeof value.createdAt !== "number") return false;
  if (!("spaceId" in value) || (value.spaceId !== null && typeof value.spaceId !== "string")) {
    return false;
  }
  if (!("mimeType" in value) || typeof value.mimeType !== "string") return false;
  const audio = "audio" in value ? value.audio : undefined;
  const text = "text" in value ? value.text : undefined;
  if (audio !== undefined && !(audio instanceof ArrayBuffer)) return false;
  if (text !== undefined && typeof text !== "string") return false;
  return audio !== undefined || text !== undefined;
}

export function createDictationAudioStore(
  factory: () => IDBFactory | undefined = () => globalThis.indexedDB,
): DictationAudioStore {
  let connection: Promise<IDBDatabase> | undefined;
  const open = () =>
    (connection ??= new Promise<IDBDatabase>((resolve, reject) => {
      const idb = factory();
      if (!idb) {
        reject(new Error("IndexedDB unavailable"));
        return;
      }
      const request = idb.open(DB_NAME, 1);
      request.onupgradeneeded = () =>
        request.result.createObjectStore(STORE_NAME, { keyPath: "id" });
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error("IndexedDB blocked"));
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => {
          db.close();
          connection = undefined;
        };
        resolve(db);
      };
    }).catch((error: unknown) => {
      connection = undefined;
      throw error;
    }));
  async function run<T>(
    mode: IDBTransactionMode,
    work: (store: IDBObjectStore) => () => T,
  ): Promise<T> {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, mode);
      const result = work(transaction.objectStore(STORE_NAME));
      transaction.oncomplete = () => resolve(result());
      transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB aborted"));
      transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB failed"));
    });
  }
  return {
    put: (segment) =>
      run("readwrite", (store) => {
        store.put(segment);
        return () => undefined;
      }),
    delete: async (ids) => {
      if (!ids.length) return;
      await run("readwrite", (store) => {
        for (const id of ids) store.delete(id);
        return () => undefined;
      });
    },
    list: () =>
      run("readonly", (store) => {
        const request = store.getAll();
        return () => {
          const rows: unknown = request.result;
          return Array.isArray(rows) ? rows.filter(isStoredDictationSegment) : [];
        };
      }),
  };
}
